import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { TourCard } from '../components/tours/TourCard';
import { TourFilters } from '../components/tours/TourFilters';
import { toursAPI, categoriesAPI } from '../lib/api';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { Filter, Loader2, Search, X, Grid3X3, LayoutGrid, Mountain, Waves, Utensils, Moon, Compass, Sun, Camera, Map, Anchor, TreePine, Bike, Car, Plane, Tent, Sparkles } from 'lucide-react';
import { formatPrice, getCategoryColor, getCategoryLabel, truncateText } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Star, Clock, MapPin } from 'lucide-react';

// Icon mapping for dynamic categories
const iconMap = {
  Mountain, Waves, Utensils, Moon, Compass, Sun, Camera, Map, 
  Anchor, TreePine, Bike, Car, Plane, Tent, Sparkles, Star
};

// Compact Tour Card for grid view
const CompactTourCard = ({ tour }) => {
  // Check if all badges should be hidden
  const hideAllBadges = tour.hide_all_badges === true;
  
  return (
    <Link to={`/tours/${tour.id}`} data-testid={`tour-card-${tour.id}`}>
      <Card className="tour-card group overflow-hidden bg-white border border-border/50 rounded-xl h-full hover:shadow-lg transition-shadow duration-300">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={tour.image_url}
            alt={tour.title}
            className="tour-card-image w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {/* Category Badge - Hide if all badges hidden or category badge disabled */}
          {!hideAllBadges && tour.show_category_badge !== false && (
            <Badge 
              className={`absolute top-2 left-2 ${getCategoryColor(tour.category)} border-0 text-xs px-2 py-0.5`}
              data-testid={`tour-category-${tour.id}`}
            >
              {tour.category_badge_text || getCategoryLabel(tour.category).replace(' Tours', '')}
            </Badge>
          )}
          <div className="absolute bottom-2 right-2 bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full font-bold text-sm">
            {formatPrice(tour.price)}
          </div>
        </div>
        <div className="p-3">
          <h3 className="font-heading text-sm font-semibold text-foreground mb-1 group-hover:text-primary transition-colors line-clamp-1">
            {tour.title}
          </h3>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                <span>{tour.duration}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <MapPin className="h-3 w-3" />
                <span className="truncate max-w-[60px]">{tour.location.split(',')[0]}</span>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <Star className="h-3 w-3 fill-secondary text-secondary" />
              <span className="font-medium text-foreground">{tour.rating}</span>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

const fallbackCategoryTabs = [
  { id: '', slug: '', name: 'All', icon: null },
  { id: 'adventure', slug: 'adventure', name: 'Adventure', icon: 'Mountain' },
  { id: 'marine', slug: 'marine', name: 'Marine', icon: 'Waves' },
  { id: 'food', slug: 'food', name: 'Food', icon: 'Utensils' },
  { id: 'night', slug: 'night', name: 'Night', icon: 'Moon' },
];

export default function ToursPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const [categoryTabs, setCategoryTabs] = useState(fallbackCategoryTabs);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('compact'); // 'compact' or 'standard'
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const { settings } = useSettings();
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    maxPrice: 200,
    search: searchParams.get('search') || '',
  });

  // Get colors from settings
  const toursBgColor = settings?.tours_bg_color || '#f9fafb';

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        if (response.data && response.data.length > 0) {
          // Add "All" option at the beginning
          const allOption = { id: '', slug: '', name: 'All', icon: null };
          setCategoryTabs([allOption, ...response.data]);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    const searchFromUrl = searchParams.get('search');
    setFilters(prev => ({ 
      ...prev, 
      category: categoryFromUrl || '',
      search: searchFromUrl || ''
    }));
    setSearchInput(searchFromUrl || '');
  }, [searchParams]);

  useEffect(() => {
    const loadTours = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.category) params.category = filters.category;
        if (filters.maxPrice < 200) params.max_price = filters.maxPrice;
        if (filters.search) params.search = filters.search;
        
        const response = await toursAPI.getAll(params);
        setTours(response.data);
      } catch (error) {
        console.error('Error loading tours:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTours();
  }, [filters]);

  const handleClearFilters = () => {
    setFilters({ category: '', maxPrice: 200, search: '' });
    setSearchInput('');
    setSearchParams({});
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    const params = {};
    if (newFilters.category) params.category = newFilters.category;
    if (newFilters.search) params.search = newFilters.search;
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFiltersChange({ ...filters, search: searchInput });
  };

  const handleCategoryClick = (categoryId) => {
    const newFilters = { ...filters, category: categoryId };
    handleFiltersChange(newFilters);
  };

  const getCategoryTitle = () => {
    if (filters.search) return `Search: "${filters.search}"`;
    if (!filters.category) return 'All Tours';
    const titles = {
      adventure: 'Adventure Tours',
      marine: 'Marine Tours',
      food: 'Food Tours',
      night: 'Night Tours',
    };
    return titles[filters.category] || 'All Tours';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: toursBgColor }}>
      <Navbar />
      
      {/* Header with Search */}
      <section className="py-8" style={{ backgroundColor: settings?.hero_bg_color || '#166534' }} data-testid="tours-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            {getCategoryTitle()}
          </h1>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tours..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 bg-white/90 border-0 rounded-full"
                data-testid="tours-search-input"
              />
            </div>
            <Button type="submit" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6">
              Search
            </Button>
          </form>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="bg-white border-b border-border sticky top-16 z-30" data-testid="category-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {categoryTabs.map((cat) => {
                const IconComponent = cat.icon ? (iconMap[cat.icon] || Compass) : null;
                return (
                  <button
                    key={cat.id || cat.slug}
                    onClick={() => handleCategoryClick(cat.slug || cat.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      filters.category === (cat.slug || cat.id)
                        ? 'bg-primary text-white'
                        : 'bg-muted hover:bg-muted/80 text-foreground'
                    }`}
                    data-testid={`category-tab-${cat.slug || cat.id || 'all'}`}
                  >
                    {IconComponent && <IconComponent className="h-4 w-4" />}
                    {cat.name || cat.label}
                  </button>
                );
              })}
            </div>
            
            {/* View Toggle & Filter */}
            <div className="flex items-center gap-2 ml-4">
              <div className="hidden md:flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('compact')}
                  className={`p-2 rounded ${viewMode === 'compact' ? 'bg-white shadow-sm' : ''}`}
                  title="Compact view"
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('standard')}
                  className={`p-2 rounded ${viewMode === 'standard' ? 'bg-white shadow-sm' : ''}`}
                  title="Standard view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
              
              {/* Mobile Filter */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden rounded-full" data-testid="mobile-filter-btn">
                    <Filter className="h-4 w-4 mr-1" />
                    Filter
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-6">
                  <TourFilters 
                    filters={filters} 
                    setFilters={handleFiltersChange} 
                    onClear={handleClearFilters}
                  />
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </section>

      {/* Active Filters Display */}
      {(filters.search || filters.category || filters.maxPrice < 200) && (
        <section className="bg-muted/30 py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {filters.search && (
                <Badge variant="secondary" className="gap-1">
                  Search: {filters.search}
                  <button onClick={() => handleFiltersChange({ ...filters, search: '' })}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filters.category && (
                <Badge variant="secondary" className="gap-1">
                  {getCategoryLabel(filters.category)}
                  <button onClick={() => handleFiltersChange({ ...filters, category: '' })}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filters.maxPrice < 200 && (
                <Badge variant="secondary" className="gap-1">
                  Max: ${filters.maxPrice}
                  <button onClick={() => handleFiltersChange({ ...filters, maxPrice: 200 })}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <button 
                onClick={handleClearFilters}
                className="text-sm text-primary hover:underline"
              >
                Clear all
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-8" data-testid="tours-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-36">
                <TourFilters 
                  filters={filters} 
                  setFilters={handleFiltersChange} 
                  onClear={handleClearFilters}
                />
              </div>
            </aside>

            {/* Tours Grid */}
            <div className="flex-1">
              <div className="flex justify-between items-center mb-4">
                <p className="text-muted-foreground text-sm" data-testid="tours-count">
                  {loading ? 'Loading...' : `${tours.length} tours found`}
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : tours.length === 0 ? (
                <div className="text-center py-20" data-testid="no-tours-message">
                  <p className="text-lg text-muted-foreground mb-4">
                    No tours found matching your criteria.
                  </p>
                  <Button onClick={handleClearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </div>
              ) : viewMode === 'compact' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="tours-grid">
                  {tours.map((tour) => (
                    <CompactTourCard key={tour.id} tour={tour} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="tours-grid">
                  {tours.map((tour) => (
                    <TourCard key={tour.id} tour={tour} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
